import styled, { css } from 'styled-components';
import { Text } from '@ardoq/typography';
import { colors, zIndex } from '@ardoq/design-tokens';
import { FlexBox, Inline } from '@ardoq/layout';
import { ButtonSize, ButtonType, IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { UnstyledLink } from '@ardoq/link';
import {
  getFocusRingBorderCSS,
  getFocusRingSpreadCSS,
} from '@ardoq/style-helpers';
import {
  Badge,
  InteractiveBadgeProps,
  ReadonlyBadgeProps,
} from '@ardoq/status-ui';
import { PropsReturningActions } from '@ardoq/rxbeach';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { useEffect, useId, useRef, useState } from 'react';
import { usePrevious } from '@ardoq/hooks';
import { useDropdownTriggerContainer } from '@ardoq/dropdown-2026';
import { Popover2025 } from '@ardoq/popovers';
import { NewPageTopBarActionButton } from './NewPageTopBarActionButton';
import { HelpMenu } from './HelpMenu';
import {
  getHelpPanelTitle,
  HELP_PANEL_ICON_NAME,
} from './HelpPanel/HelpPanelFrame';
import {
  ActionMenuButton,
  ActionMenuDivider,
  ActionMenuDropdownButton,
  ActionMenuIconButton,
  ActionMenuItem,
  ActionMenuLinkButton,
  BreadcrumbItem,
  CommonActionMenuItemProps,
  HelpItem,
  ToggleSidebarButtonProps,
} from './types';

export const NEW_PAGE_TOP_BAR_HEIGHT = 50;

const TopBarWrapper = styled(FlexBox)<{ $hasSidePanelColumn: boolean }>`
  ${({ $hasSidePanelColumn }) =>
    !$hasSidePanelColumn &&
    css`
      min-height: ${NEW_PAGE_TOP_BAR_HEIGHT}px;
      border-bottom: 1px solid ${colors.borderSubtle};
      z-index: ${zIndex.ABOVE_GRID_EDITOR_BACKGROUND};
    `}
`;

const Left = styled(FlexBox)`
  flex: 1 1 0;
  min-width: 0;
  overflow: clip;
  ${getFocusRingSpreadCSS}
`;

const BREADCRUMB_MIN_WIDTH = '2em';

const BreadcrumbList = styled(Inline)`
  flex-wrap: nowrap;
  min-width: 0;
`;

const BreadcrumbListItem = styled(FlexBox)<{ $isCurrentPageCrumb: boolean }>`
  min-width: ${({ $isCurrentPageCrumb }) =>
    $isCurrentPageCrumb ? 0 : BREADCRUMB_MIN_WIDTH};
  /* Parent crumbs take almost all of the shrinking, so the current page name shortens last. */
  flex-shrink: ${({ $isCurrentPageCrumb }) => ($isCurrentPageCrumb ? 1 : 100)};
`;

const BreadcrumbSeparator = styled(Text)`
  flex-shrink: 0;
  color: ${colors.textSubtle};
`;

const BreadcrumbButton = styled(UnstyledLink)`
  min-width: 0;
  cursor: pointer;
  color: ${colors.textSubtle};
  &:focus-visible {
    ${getFocusRingBorderCSS()}
  }
`;

export type NewPageTopBarProps = {
  toggleSidebarButton: ToggleSidebarButtonProps | null;
  breadcrumbs: BreadcrumbItem[];
  /** Rendered next to the page title. */
  titleActionItems?: ActionMenuItem[];
  actionItems: ActionMenuItem[];
  /** Rendered after the Help button. */
  trailingActionItems?: ActionMenuItem[];
  helpButtonItems?: HelpItem[];
  badges?: (ReadonlyBadgeProps | InteractiveBadgeProps)[];
  /** Drawn inside the side panel column's shell row, which owns the height, bottom border, stacking and trailing buttons. */
  hasSidePanelColumn?: boolean;
  i18nLocale?: I18nLocale;
};

export const toTopBarIconButtonProps = ({
  buttonType,
  ...action
}: CommonActionMenuItemProps & {
  onClick: VoidFunction;
  icon: {
    name: IconName;
  };
}): ActionMenuIconButton => {
  return {
    type: 'icon-button',
    buttonType: buttonType ?? ButtonType.GHOST,
    ...action,
  };
};

export const toTopBarDropdownProps = ({
  buttonType,
  ...action
}: CommonActionMenuItemProps & {
  isIconOnly?: boolean;
  options: DropdownItem[];
  maxDropdownSize?: DropdownSize;
  onMenuOpen?: VoidFunction;
}): ActionMenuDropdownButton => {
  return {
    type: 'dropdown',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarActionButtonProps = ({
  buttonType,
  ...action
}: CommonActionMenuItemProps & {
  onClick: VoidFunction;
  tooltipText?: string;
  buttonSize?: ButtonSize;
  badgeAnchorId?: string;
}): ActionMenuButton => {
  return {
    type: 'button',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarActionButtonPropsReturningActions = ({
  buttonType,
  ...action
}: PropsReturningActions<
  CommonActionMenuItemProps & {
    onClick: VoidFunction;
    tooltipText?: string;
  }
>): PropsReturningActions<ActionMenuButton> => {
  return {
    type: 'button',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarDropdownPropsReturningActions = ({
  buttonType,
  ...action
}: PropsReturningActions<
  CommonActionMenuItemProps & {
    isIconOnly?: boolean;
    options: DropdownItem[];
    maxDropdownSize?: DropdownSize;
    onMenuOpen?: VoidFunction;
  }
>): PropsReturningActions<ActionMenuDropdownButton> => {
  return {
    type: 'dropdown',
    buttonType: buttonType ?? ButtonType.TERTIARY,
    ...action,
  };
};

export const toTopBarLinkButtonProps = ({
  ...action
}: Omit<ActionMenuLinkButton, 'type'>): ActionMenuLinkButton => ({
  type: 'link',
  target: '_self',
  ...action,
});

export const toTopBarActionDividerProps = (): ActionMenuDivider => ({
  type: 'divider',
  size: 'default',
});

/** Separates the page's actions from the global ones. */
export const toTopBarSectionDividerProps = (): ActionMenuDivider => ({
  type: 'divider',
  size: 'section',
});

const HelpDropdown = ({
  items,
  i18nLocale,
}: {
  items: HelpItem[];
  i18nLocale: I18nLocale;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useDropdownTriggerContainer<HTMLButtonElement>({
    isOpen,
    openDropdown: () => setIsOpen(true),
  });
  const popoverId = useId();

  return (
    <>
      <IconButton
        ref={triggerRef}
        label={t(
          i18nLocale,
          'pageLayout:newPageTopBar.helpButtonLabel',
          'Help'
        )}
        iconName={IconName.HELP}
        isActive={isOpen}
        onClick={() => setIsOpen(open => !open)}
        aria-controls={popoverId}
        aria-expanded={isOpen}
        data-tooltip-text={isOpen ? '' : undefined}
      />
      <Popover2025
        id={popoverId}
        isOpen={isOpen}
        anchorRef={triggerRef}
        onClose={() => setIsOpen(false)}
        onClickOutside={() => setIsOpen(false)}
        placement={{ side: 'bottom', overflow: 'left' }}
      >
        <HelpMenu items={items} onItemSelected={() => setIsOpen(false)} />
      </Popover2025>
    </>
  );
};

export const HELP_SIDE_PANEL_ID = 'help-side-panel';

type HelpPanelButtonProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  i18nLocale: I18nLocale;
};

export const HelpPanelButton = ({
  isOpen,
  onOpenChange,
  i18nLocale,
}: HelpPanelButtonProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpen = usePrevious(isOpen);

  useEffect(() => {
    if (wasOpen && !isOpen) {
      triggerRef.current?.focus();
    }
  }, [isOpen, wasOpen]);

  return (
    <IconButton
      ref={triggerRef}
      label={getHelpPanelTitle(i18nLocale)}
      iconName={HELP_PANEL_ICON_NAME}
      isActive={isOpen}
      onClick={() => onOpenChange(!isOpen)}
      aria-controls={HELP_SIDE_PANEL_ID}
      aria-expanded={isOpen}
    />
  );
};

export const NewPageTopBar = ({
  toggleSidebarButton,
  breadcrumbs,
  titleActionItems = [],
  actionItems,
  trailingActionItems = [],
  helpButtonItems = [],
  badges = [],
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hasSidePanelColumn = false,
}: NewPageTopBarProps) => {
  return (
    <TopBarWrapper
      $hasSidePanelColumn={hasSidePanelColumn}
      role="banner"
      /** Todo: Change to medium */
      paddingX="large"
      paddingRight={hasSidePanelColumn ? 'small' : undefined}
      align="center"
      backgroundColor="surfaceDefault"
      gap="small"
      width="full"
    >
      <Left gap="small" align="center">
        {toggleSidebarButton !== null && (
          <FlexBox flexShrink={0}>
            <IconButton
              label={t(
                i18nLocale,
                'pageLayout:newPageTopBar.openMenu',
                'Open menu'
              )}
              buttonSize={ButtonSize.SMALL}
              iconName={IconName.SIDEBAR_OPEN}
              onClick={toggleSidebarButton.onClick}
              onMouseEnter={toggleSidebarButton.onHover}
            />
          </FlexBox>
        )}

        <FlexBox
          gap="small"
          align="center"
          minWidth="0"
          as="nav"
          aria-label={t(
            i18nLocale,
            'pageLayout:newPageTopBar.breadcrumbAriaLabel',
            'Breadcrumb'
          )}
        >
          <BreadcrumbList gap="xsmall" align="center" forwardedAs="ul">
            {breadcrumbs.map((breadcrumb, index) => {
              const isCurrentPageCrumb = index === breadcrumbs.length - 1;
              return (
                <BreadcrumbListItem
                  key={index}
                  id={breadcrumb.htmlId}
                  gap="xsmall"
                  align="center"
                  forwardedAs="li"
                  aria-current={isCurrentPageCrumb ? 'page' : undefined}
                  $isCurrentPageCrumb={isCurrentPageCrumb}
                >
                  {index > 0 && (
                    <BreadcrumbSeparator variant="text2" aria-hidden="true">
                      /
                    </BreadcrumbSeparator>
                  )}
                  {isCurrentPageCrumb ? (
                    <Text variant="text2Bold" truncate>
                      {breadcrumb.label}
                    </Text>
                  ) : (
                    <BreadcrumbButton
                      onClick={breadcrumb.onClick}
                      href={breadcrumb.href}
                    >
                      <Text variant="text2" truncate>
                        {breadcrumb.label}
                      </Text>
                    </BreadcrumbButton>
                  )}
                </BreadcrumbListItem>
              );
            })}
          </BreadcrumbList>
          <FlexBox gap="xsmall" flexShrink={0}>
            {badges.map((badge, index) => (
              <Badge key={index} {...badge} />
            ))}
          </FlexBox>
        </FlexBox>
        {titleActionItems.length > 0 && (
          <FlexBox gap="small" align="center" flexShrink={0}>
            {titleActionItems.map((action, index) => (
              <NewPageTopBarActionButton key={index} action={action} />
            ))}
          </FlexBox>
        )}
      </Left>

      <FlexBox align="center" gap="small" flexShrink={0}>
        {actionItems.map((action, index) => (
          <NewPageTopBarActionButton key={index} action={action} />
        ))}
        {helpButtonItems.length > 0 && (
          <HelpDropdown items={helpButtonItems} i18nLocale={i18nLocale} />
        )}
        {trailingActionItems.map((action, index) => (
          <NewPageTopBarActionButton key={index} action={action} />
        ))}
      </FlexBox>
    </TopBarWrapper>
  );
};
